import { z } from 'zod'
import {
  AnalyticsSource,
  ArtifactAvailability,
  ArtifactClass,
  BackupFileRole,
  BackupSnapshotStatus,
  DeliveryMode,
  DeliveryState,
  EnvelopeState,
  IdentityProvider,
  LedgerReason,
  LocaleCode,
  ParticipantKind,
  PurchaseProvider,
  PurchaseType,
  RecipientState,
  RoutingMode,
  SenderOrder,
  ShareAuthority,
  ShareObservationType,
  SignatureMethod,
  SignatureType,
  StorageDestinationKind,
  UserStatus,
} from './enums.js'
import { Field, Role, ReminderPolicy } from './manifest.js'
import { Formula } from './formula.js'
import { AnchorRule } from './anchor.js'
import { AttachmentRequest } from './attachment.js'
import { SCHEMA_VERSION, HASH_STRING_PATTERN } from './version.js'

const Id = z.string().min(1).max(128)
const IsoDateTime = z.string().datetime()
const Sha256Hex = z.string().regex(/^[0-9a-f]{64}$/)
const HashString = z.string().regex(HASH_STRING_PATTERN)
const OptionalId = Id.optional()

/* -------------------------------------------------------------------------- */
/* Local entities — PRD §12.1                                                 */
/* -------------------------------------------------------------------------- */

export const LocalDocumentStatus = z.enum(['ready', 'processing', 'error'])
export type LocalDocumentStatus = z.infer<typeof LocalDocumentStatus>

/**
 * Package manifest — D3 / ADR 0010. Keeps every admitted original input
 * separately from the normalized signing source and maps package pages back to
 * (original, original page). Originals are immutable and stored at
 * `<document_id>/original/<index>`; the signing source is `<document_id>/source/r1`.
 */
/** `docx`: a Word file converted to the signing PDF on the device (DEV-016). */
export const SourceOriginalFormat = z.enum(['pdf', 'jpeg', 'png', 'heic', 'docx'])
export type SourceOriginalFormat = z.infer<typeof SourceOriginalFormat>

export const SourceOriginal = z
  .object({
    ref: z.string().min(1),
    sha256: HashString,
    file_name: z.string().min(1).max(300),
    format: SourceOriginalFormat,
    page_count: z.number().int().positive().optional(),
  })
  .strict()
export type SourceOriginal = z.infer<typeof SourceOriginal>

export const PackagePageEntry = z
  .object({
    package_page_index: z.number().int().min(0),
    original_ref: z.string().min(1),
    original_page_index: z.number().int().min(0),
  })
  .strict()
export type PackagePageEntry = z.infer<typeof PackagePageEntry>

/** `combined` = one ordered signing PDF; `separate` = one document per input (D3). */
export const PackageManifestMode = z.enum(['combined', 'separate'])
export type PackageManifestMode = z.infer<typeof PackageManifestMode>

export const PackageManifest = z
  .object({
    schema_version: z.literal(SCHEMA_VERSION),
    mode: PackageManifestMode,
    originals: z.array(SourceOriginal).min(1),
    page_map: z.array(PackagePageEntry),
  })
  .strict()
export type PackageManifest = z.infer<typeof PackageManifest>

export const LocalDocument = z
  .object({
    id: Id,
    title: z.string().min(1).max(300),
    source_ref: z.string().min(1),
    source_hash: HashString,
    page_count: z.number().int().positive(),
    status: LocalDocumentStatus,
    created_at: IsoDateTime,
    updated_at: IsoDateTime,
    /** D5 organizational metadata; never changes terms, permissions or limits. */
    category: z.string().min(1).max(60).optional(),
    template_id: OptionalId,
    envelope_id: OptionalId,
    // Optional so vault records written before D3 (no package manifest) keep
    // validating unchanged; new imports always carry it (ADR 0010).
    package_manifest: PackageManifest.optional(),
  })
  .strict()
export type LocalDocument = z.infer<typeof LocalDocument>

export const LocalDraft = z
  .object({
    id: Id,
    document_id: OptionalId,
    template_id: OptionalId,
    title: z.string().min(1).max(300),
    recipients: z
      .array(
        z.object({
          role_id: z.string().min(1).max(64).optional(),
          kind: ParticipantKind,
          name: z.string().min(1).max(200),
          email: z.email().max(320).optional(),
          order_index: z.number().int().nonnegative(),
          locale_code: LocaleCode.optional(),
        }),
      )
      .max(20),
    fields: z.array(Field).max(500),
    delivery_settings: z
      .object({
        delivery_mode: DeliveryMode,
        routing_mode: RoutingMode,
        sender_order: SenderOrder,
        subject_template: z.string().max(200),
        message_template: z.string().max(2000),
        reminder_policy: ReminderPolicy.optional(),
        expiration_days: z.number().int().positive().max(90).optional(),
      })
      .strict(),
    /** D5 organizational metadata carried from the document at preparation time. */
    category: z.string().min(1).max(60).optional(),
    /** Locally entered field values; never uploaded without an explicit remote request. */
    values: z.record(z.string(), z.union([z.string(), z.boolean()])).optional(),
    /** Local signature/initials artifacts adopted into this draft (S1.4). */
    adopted_signature_ids: z.array(Id).max(20).optional(),
    /** D8 anchor rules for this draft's fields, resolved per source revision. */
    anchor_rules: z.array(AnchorRule).max(200).optional(),
    /**
     * D10 recipient attachment requests configured on this draft (S5.8). Local-only until the
     * draft is created remotely, where the service re-validates every request against the
     * register and the plan before anything is written.
     */
    attachment_requests: z.array(AttachmentRequest).max(20).optional(),
    /**
     * Set on the copy "Send again" makes: the retired envelope the copy's request replaces. Every
     * tab that sends this copy names it on create, and the service lets only one envelope replace
     * a given one, so two copies of one restart never both go out.
     */
    replaces_envelope_id: OptionalId,
    autosave_version: z.number().int().nonnegative(),
    last_opened_at: IsoDateTime,
    created_at: IsoDateTime,
    updated_at: IsoDateTime,
  })
  .strict()
export type LocalDraft = z.infer<typeof LocalDraft>

export const Template = z
  .object({
    id: Id,
    schema_version: z.literal(SCHEMA_VERSION),
    title: z.string().min(1).max(300),
    source_ref: z.string().min(1),
    source_hash: HashString,
    roles: z.array(Role).max(50),
    fields: z.array(Field).max(500),
    local_defaults: z.record(z.string(), z.unknown()).optional(),
    favorite: z.boolean(),
    created_at: IsoDateTime,
    updated_at: IsoDateTime,
    last_used_at: IsoDateTime.optional(),
    deleted_at: IsoDateTime.optional(),
  })
  .strict()
export type Template = z.infer<typeof Template>

export const Workflow = z
  .object({
    id: Id,
    template_id: Id,
    /**
     * The template revision this workflow pinned when it was created (§12.1, AT-027). Absent means
     * "follow the template's current revision"; a repeat send always reviews before sending either
     * way, and the template master is never modified by a workflow.
     */
    template_revision: z.number().int().positive().optional(),
    title: z.string().min(1).max(300),
    routing_mode: RoutingMode,
    role_order: z.array(z.string().min(1).max(64)),
    delivery_mode: DeliveryMode,
    sender_order: SenderOrder,
    subject_template: z.string().max(200),
    message_template: z.string().max(2000),
    reminder_policy: ReminderPolicy,
    expiration_policy: z
      .object({
        expiration_days: z.number().int().positive().max(90),
      })
      .strict(),
    cc_rules: z
      .array(
        z.object({
          role_id: z.string().min(1).max(64),
          required: z.boolean(),
        }),
      )
      .max(5),
    created_at: IsoDateTime,
    updated_at: IsoDateTime,
  })
  .strict()
export type Workflow = z.infer<typeof Workflow>

export const MyInfo = z
  .object({
    display_name: z.string().max(200),
    initials: z.string().max(10),
    /** Optional: My Info is useful with only a name, and the email is offered from the account. */
    email: z.email().max(320).optional(),
    phone: z.string().max(40).optional(),
    company: z.string().max(200).optional(),
    title: z.string().max(200).optional(),
    address: z.string().max(500).optional(),
    date_format: z.string().min(1).max(40),
    updated_at: IsoDateTime,
  })
  .strict()
export type MyInfo = z.infer<typeof MyInfo>

/**
 * Local completion record — S1.4 / LOC-01.
 * A completed artifact is a *separate* output produced from the immutable signing
 * source plus the accepted values; the source is never rewritten. `local_time_label`
 * marks device-reported time honestly (no service attestation exists locally).
 */
export const LocalCompletion = z
  .object({
    id: Id,
    document_id: Id,
    template_id: OptionalId,
    /** Vault blob key of the composed output. */
    output_ref: z.string().min(1),
    output_hash: HashString,
    source_hash: HashString,
    /** Domain-separated digest over the filled values and adopted signatures. */
    field_values_digest: HashString,
    signature_ids: z.array(Id).max(20),
    composer_version: z.string().min(1),
    page_count: z.number().int().positive(),
    completed_at: IsoDateTime,
    local_time_label: z.string().min(1),
    /** Evaluated formula evidence captured with the completion (S1.9, D9 rule 7). */
    formulas: z.array(Formula).max(100).optional(),
    /** Per-participant adoption records for in-person signing (S1.5, LOC-07). */
    participant_adoptions: z
      .array(
        z
          .object({
            role_id: z.string().min(1).max(64),
            name: z.string().min(1).max(200),
            method: SignatureMethod,
            consent_version: z.string().min(1).max(40),
            consent_locale: LocaleCode,
            adopted_at: IsoDateTime,
          })
          .strict(),
      )
      .max(20)
      .optional(),
  })
  .strict()
export type LocalCompletion = z.infer<typeof LocalCompletion>

export const LocalSignature = z
  .object({
    id: Id,
    type: SignatureType,
    method: SignatureMethod,
    /** Ciphertext or opaque payload reference — never plaintext in diagnostics. */
    encrypted_payload: z.string().min(1),
    created_at: IsoDateTime,
    last_used_at: IsoDateTime,
  })
  .strict()
export type LocalSignature = z.infer<typeof LocalSignature>

export const RecentRecipient = z
  .object({
    local_id: Id,
    name: z.string().min(1).max(200),
    email: z.email().max(320),
    last_used_at: IsoDateTime,
  })
  .strict()
export type RecentRecipient = z.infer<typeof RecentRecipient>

export const DiagnosticsSeverity = z.enum(['debug', 'info', 'warn', 'error'])
export type DiagnosticsSeverity = z.infer<typeof DiagnosticsSeverity>

export const LocalDiagnosticsEvent = z
  .object({
    timestamp: IsoDateTime,
    event_code: z.string().min(1).max(64),
    severity: DiagnosticsSeverity,
    /** Sanitized only — never document bytes, tokens, or recipient PII (§15.5). */
    context: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
    app_version: z.string().max(40),
    platform: z.string().max(40),
  })
  .strict()
export type LocalDiagnosticsEvent = z.infer<typeof LocalDiagnosticsEvent>

export const BackupManifest = z
  .object({
    schema_version: z.literal(SCHEMA_VERSION),
    created_at: IsoDateTime,
    app_version: z.string().max(40),
    item_hashes: z.record(z.string(), HashString),
    encryption_metadata: z
      .object({
        profile: z.string().min(1).max(64),
        kdf: z.string().min(1).max(64),
        cipher: z.string().min(1).max(64),
      })
      .strict(),
    item_counts: z.record(z.string(), z.number().int().nonnegative()),
  })
  .strict()
export type BackupManifest = z.infer<typeof BackupManifest>

export const LocalAnalyticsEvent = z
  .object({
    event_id: Id,
    occurred_at: IsoDateTime,
    agreement_id: Id,
    event_type: z.string().min(1).max(64),
    template_id: OptionalId,
    page_count: z.number().int().positive().optional(),
    duration_ms: z.number().int().nonnegative().optional(),
    source: AnalyticsSource,
    schema_version: z.literal(SCHEMA_VERSION),
  })
  .strict()
export type LocalAnalyticsEvent = z.infer<typeof LocalAnalyticsEvent>

export const LocalAggregateCache = z
  .object({
    schema_version: z.literal(SCHEMA_VERSION),
    last_applied_event: Id,
    inventory_counts: z.record(z.string(), z.number().int().nonnegative()),
    per_period_activity: z.record(z.string(), z.number().int().nonnegative()),
    template_usage: z.record(z.string(), z.number().int().nonnegative()),
  })
  .strict()
export type LocalAggregateCache = z.infer<typeof LocalAggregateCache>

export const BackupStatus = z
  .object({
    destination_type: StorageDestinationKind,
    last_success_at: IsoDateTime.optional(),
    last_error_code: z.string().max(64).optional(),
    last_error_scope: z.string().max(64).optional(),
  })
  .strict()
export type BackupStatus = z.infer<typeof BackupStatus>

/* -------------------------------------------------------------------------- */
/* Server entities — PRD §12.2                                                */
/* -------------------------------------------------------------------------- */

export const User = z
  .object({
    id: Id,
    primary_email_normalized: z.email().max(320).optional(),
    status: UserStatus,
    created_at: IsoDateTime,
    last_seen_at: IsoDateTime.optional(),
  })
  .strict()
export type User = z.infer<typeof User>

export const Identity = z
  .object({
    id: Id,
    user_id: Id,
    provider: IdentityProvider,
    provider_subject: z.string().min(1).max(255),
    verified_email: z.email().max(320).optional(),
    verified_at: IsoDateTime.optional(),
    linked_at: IsoDateTime,
  })
  .strict()
export type Identity = z.infer<typeof Identity>

export const Entitlement = z
  .object({
    user_id: Id,
    derived_lifetime_unlocked: z.boolean(),
    valid_purchase_refs: z.array(Id),
    verified_at: IsoDateTime.optional(),
    updated_at: IsoDateTime,
  })
  .strict()
export type Entitlement = z.infer<typeof Entitlement>

export const CreditLedgerEntry = z
  .object({
    id: Id,
    user_id: Id,
    delta: z.number().int(),
    reason: LedgerReason,
    envelope_id: OptionalId,
    purchase_txn_id: OptionalId,
    grant_id: OptionalId,
    allocation_refs: z.array(Id),
    reverses_entry_id: OptionalId,
    idempotency_key: z.string().min(1).max(255),
    created_at: IsoDateTime,
  })
  .strict()
export type CreditLedgerEntry = z.infer<typeof CreditLedgerEntry>

/** Reservations are modeled separately from the posted credit ledger — §12.5. */
export const CreditReservation = z
  .object({
    id: Id,
    user_id: Id,
    envelope_id: Id,
    amount: z.number().int().positive(),
    status: z.enum(['held', 'consumed', 'released', 'expired']),
    created_at: IsoDateTime,
    expires_at: IsoDateTime,
    released_at: IsoDateTime.optional(),
  })
  .strict()
export type CreditReservation = z.infer<typeof CreditReservation>

export const Envelope = z
  .object({
    id: Id,
    user_id: Id,
    draft_id: Id,
    source_hash: HashString,
    source_blob_key: z.string().min(1),
    workspace_item_id: OptionalId,
    workspace_revision: z.number().int().positive().optional(),
    final_blob_key: z.string().min(1).optional(),
    final_hash: HashString.optional(),
    state: EnvelopeState,
    delivery_mode: DeliveryMode,
    routing_mode: RoutingMode,
    sender_order: SenderOrder,
    activated_at: IsoDateTime.optional(),
    expires_at: IsoDateTime.optional(),
    last_required_response_at: IsoDateTime.optional(),
    finalization_deadline: IsoDateTime.optional(),
    completed_at: IsoDateTime.optional(),
    final_download_deadline: IsoDateTime.optional(),
    artifact_class: ArtifactClass.optional(),
    revision: z.number().int().nonnegative(),
    source_manifest_digest: Sha256Hex.optional(),
    field_manifest_digest: Sha256Hex.optional(),
    response_set_version: z.number().int().nonnegative().optional(),
    response_set_digest: Sha256Hex.optional(),
  })
  .strict()
export type Envelope = z.infer<typeof Envelope>

export const Recipient = z
  .object({
    id: Id,
    envelope_id: Id,
    role_id: z.string().min(1).max(64).optional(),
    kind: ParticipantKind,
    name: z.string().min(1).max(200),
    email: z.email().max(320).optional(),
    order_index: z.number().int().nonnegative(),
    state: RecipientState,
    invitation_token_hash: z.string().min(1).optional(),
    capability_version: z.number().int().positive(),
    locale_code: LocaleCode.optional(),
    viewed_at: IsoDateTime.optional(),
    submitted_at: IsoDateTime.optional(),
    delivery_state: DeliveryState,
    first_presented_at: IsoDateTime.optional(),
  })
  .strict()
export type Recipient = z.infer<typeof Recipient>

export const ResponseObject = z
  .object({
    id: Id,
    envelope_id: Id,
    recipient_id: Id,
    blob_key: z.string().min(1),
    ciphertext_hash: Sha256Hex,
    response_version: z.number().int().positive(),
    created_at: IsoDateTime,
  })
  .strict()
export type ResponseObject = z.infer<typeof ResponseObject>

export const AuditEventType = z.enum([
  'created',
  'activated',
  'manual_share_intent',
  'invitation_queued',
  'invitation_dispatched',
  'invitation_provider_result',
  'bootstrap_observed',
  'document_presented',
  'consent_accepted',
  'response_accepted',
  'recipient_corrected',
  'deadline_extended',
  // S5.8/D10: an accepted attachment is immutable evidence, and the manifest — not the signed
  // PDF — is what the completion path records.
  'attachment_accepted',
  'attachment_replaced',
  'attachment_access_authorized',
  // S5.8: the committed trigger for the sender's collected-attachment notice. The worker's pass
  // reads this outbox row, so the notice exists because the collection was accepted in the same
  // transaction — never because a sweep guessed it.
  'attachment_notice_queued',
  'reminder_queued',
  'reminder_dispatched',
  'reminder_skipped',
  'declined',
  'voided',
  // The sender retired a preparation that never went out, so its draft can be sent again.
  'preparation_discarded',
  'expired',
  'finalization_started',
  'finalization_failed',
  'completed',
  'final_download_authorized',
  'artifact_deleted',
])
export type AuditEventType = z.infer<typeof AuditEventType>

export const AuditEvent = z
  .object({
    id: Id,
    envelope_id: Id,
    recipient_id: OptionalId,
    event_type: AuditEventType,
    occurred_at: IsoDateTime,
    sequence: z.number().int().nonnegative(),
    actor_kind: z.enum(['sender', 'recipient', 'system']),
    actor_reference: z.string().max(200),
    sanitized_metadata: z.record(z.string(), z.unknown()),
    prev_hash: Sha256Hex.optional(),
    event_hash: Sha256Hex.optional(),
  })
  .strict()
export type AuditEvent = z.infer<typeof AuditEvent>

export const EmailJobStatus = z.enum([
  'pending',
  'claimed',
  'accepted',
  'unknown',
  'failed',
  'suppressed',
  'skipped',
])
export type EmailJobStatus = z.infer<typeof EmailJobStatus>

export const EmailJob = z
  .object({
    id: Id,
    envelope_id: Id,
    recipient_id: Id,
    type: z.enum(['invitation', 'reminder', 'completion', 'correction']),
    due_at: IsoDateTime,
    status: EmailJobStatus,
    provider_message_id: z.string().max(255).optional(),
    attempt_count: z.number().int().nonnegative(),
    logical_message_id: z.string().min(1).max(255),
  })
  .strict()
export type EmailJob = z.infer<typeof EmailJob>

export const IdempotencyRecord = z
  .object({
    scope: z.string().min(1).max(128),
    key: z.string().min(1).max(255),
    request_hash: Sha256Hex,
    response_ref: z.string().min(1),
    created_at: IsoDateTime,
    expires_at: IsoDateTime,
  })
  .strict()
export type IdempotencyRecord = z.infer<typeof IdempotencyRecord>

export const PurchaseTransaction = z
  .object({
    id: Id,
    user_id: Id,
    provider: PurchaseProvider,
    environment: z.enum(['sandbox', 'production']),
    product_id: z.string().min(1).max(128),
    purchase_type: PurchaseType,
    provider_transaction_id: z.string().min(1).max(255),
    original_transaction_id: z.string().max(255).optional(),
    state: z.enum(['pending', 'purchased', 'restored', 'revoked', 'failed']),
    verification_state: z.enum(['unverified', 'verified', 'failed']),
    amount: z.number().nonnegative().optional(),
    currency: z.string().length(3).optional(),
    checkout_session_ref: z.string().max(255).optional(),
    verified_at: IsoDateTime.optional(),
    revoked_at: IsoDateTime.optional(),
    created_at: IsoDateTime,
  })
  .strict()
export type PurchaseTransaction = z.infer<typeof PurchaseTransaction>

/* -------------------------------------------------------------------------- */
/* Storage / share / checkout — PRD §12.6                                     */
/* -------------------------------------------------------------------------- */

export const PermissionState = z.enum(['unknown', 'granted', 'denied', 'revoked', 'limited'])
export type PermissionState = z.infer<typeof PermissionState>

export const StorageDestination = z
  .object({
    id: Id,
    kind: StorageDestinationKind,
    provider_label: z.string().max(120).optional(),
    /** Opaque platform locator — never in diagnostics or backup plaintext. */
    opaque_locator: z.string().min(1),
    permission_state: PermissionState,
    auto_backup_enabled: z.boolean(),
    created_at: IsoDateTime,
    last_verified_at: IsoDateTime.optional(),
    last_success_at: IsoDateTime.optional(),
    last_error_code: z.string().max(64).optional(),
  })
  .strict()
export type StorageDestination = z.infer<typeof StorageDestination>

export const BackupSnapshotState = z
  .object({
    vault_id: Id,
    local_revision: z.number().int().nonnegative(),
    destination_id: Id,
    status: BackupSnapshotStatus,
    file_role: BackupFileRole,
    started_at: IsoDateTime,
    completed_at: IsoDateTime.optional(),
    snapshot_hash: Sha256Hex.optional(),
    error_code: z.string().max(64).optional(),
  })
  .strict()
export type BackupSnapshotState = z.infer<typeof BackupSnapshotState>

export const ShareObservation = z
  .object({
    event_id: Id,
    agreement_id: Id.optional(),
    envelope_id: Id.optional(),
    type: ShareObservationType,
    authority: ShareAuthority,
    occurred_at: IsoDateTime,
    channel: z.string().max(64).optional(),
    schema_version: z.literal(SCHEMA_VERSION),
  })
  .strict()
export type ShareObservation = z.infer<typeof ShareObservation>

export const CheckoutContext = z
  .object({
    id: Id,
    user_id: OptionalId,
    source_screen: z.string().min(1).max(120),
    draft_id: OptionalId,
    intended_product_id: z.string().min(1).max(128),
    provider: PurchaseProvider,
    state: z.enum(['created', 'pending', 'completed', 'abandoned', 'failed']),
    return_action: z.string().max(120),
    created_at: IsoDateTime,
    updated_at: IsoDateTime,
  })
  .strict()
export type CheckoutContext = z.infer<typeof CheckoutContext>

/* -------------------------------------------------------------------------- */
/* Extensions — PRD §12.5                                                     */
/* -------------------------------------------------------------------------- */

export const ArtifactRef = z
  .object({
    key: z.string().min(1),
    availability: ArtifactAvailability,
    sha256: Sha256Hex.optional(),
    deadline: IsoDateTime.optional(),
  })
  .strict()
export type ArtifactRef = z.infer<typeof ArtifactRef>

export const CapabilityScope = z
  .object({
    envelope_id: Id,
    recipient_id: Id,
    capability_version: z.number().int().positive(),
    scopes: z.array(z.string()).max(32),
    expires_at: IsoDateTime,
  })
  .strict()
export type CapabilityScope = z.infer<typeof CapabilityScope>

export const FinalizerLease = z
  .object({
    envelope_id: Id,
    lease_id: z.string().min(1),
    fencing_version: z.number().int().positive(),
    holder: z.string().min(1).max(128),
    acquired_at: IsoDateTime,
    expires_at: IsoDateTime,
  })
  .strict()
export type FinalizerLease = z.infer<typeof FinalizerLease>

/** Connected Insights summary metadata — PRD §12.3. Metadata only. */
export const ConnectedSummary = z
  .object({
    envelope_id: Id,
    status: EnvelopeState,
    delivery_state: DeliveryState,
    locale_code: LocaleCode.optional(),
    last_event_at: IsoDateTime,
    created_at: IsoDateTime,
    sent_at: IsoDateTime.optional(),
    delivered_at: IsoDateTime.optional(),
    first_opened_at: IsoDateTime.optional(),
    completed_at: IsoDateTime.optional(),
    expired_at: IsoDateTime.optional(),
    signer_count: z.number().int().nonnegative(),
    pending_signer_count: z.number().int().nonnegative(),
    reminder_count: z.number().int().nonnegative(),
    last_refreshed_at: IsoDateTime,
  })
  .strict()
export type ConnectedSummary = z.infer<typeof ConnectedSummary>
